import _ from 'lodash';
import type { Field, FieldPath, ImageField, ListField, ObjectField, RootDocument } from '../../types/Field';

export type IterateFieldCallbackData = {
    srcProjectId?: string;
    srcType?: string;
    parentFieldPath?: FieldPath;
    parentField?: RootDocument | Field;
    currentDepth: number;
};
export type IterateFieldCallback = (obj: Field | RootDocument, fieldPath: FieldPath, data: IterateFieldCallbackData) => boolean | void;

export type IterateData = {
    srcType?: string;
    srcProjectId?: string;
    parentFieldPath?: FieldPath;
    parentField?: RootDocument | Field;
    currentDepth: number;
    maxDepth?: number;
};

const iterateListField = ({ field, fieldPath, fn, opts }: { field: ListField; fieldPath: FieldPath; fn: IterateFieldCallback; opts: IterateData }) => {
    let res;
    _.forEach(field.items, (childField, index) => {
        res = iterateField({ field: childField, fieldPath: [...fieldPath, 'items', index], fn, opts });
        if (res === false) {
            return false;
        }
        return undefined;
    });
    return res;
};

const iterateObjectField = ({
    field,
    fieldPath,
    fn,
    opts
}: {
    field: ObjectField | ImageField;
    fieldPath: FieldPath;
    fn: IterateFieldCallback;
    opts: IterateData;
}) => {
    let res;
    _.forEach(field.fields, (childField, childFieldName) => {
        res = iterateField({ field: childField, fieldPath: [...fieldPath, 'fields', childFieldName], fn, opts });
        if (res === false) {
            return false;
        }
        return undefined;
    });
    return res;
};

export const iterateField = ({ field, fieldPath, fn, opts }: { field: Field; fieldPath: FieldPath; fn: IterateFieldCallback; opts: IterateData }) => {
    const { currentDepth = 0, maxDepth = Infinity } = opts;

    if (currentDepth > maxDepth) {
        return true;
    }

    let res = fn(field, fieldPath, _.pick(opts, ['srcType', 'srcProjectId', 'parentField', 'parentFieldPath', 'currentDepth']));
    if (res === false || res === true) {
        return res;
    }

    const nextOpts = {
        ...opts,
        parentFieldPath: fieldPath,
        parentField: field,
        currentDepth: currentDepth + 1
    };

    if (field.type === 'object' || field.type === 'image') {
        res = iterateObjectField({ field, fieldPath, fn, opts: nextOpts });
    } else if (field.type === 'list') {
        res = iterateListField({ field, fieldPath, fn, opts: nextOpts });
    }

    return res;
};

export const iterateRootField = (fields: RootDocument[], fn: IterateFieldCallback, maxDepth = Infinity) => {
    _.forEach(fields, (field) => {
        const { srcType, srcProjectId, srcObjectId } = field;
        let res = fn(field, [srcObjectId], { srcType, srcProjectId, currentDepth: 0 });

        if (res === false || res === true) {
            return res;
        }

        const childOpts: IterateData = {
            srcType,
            srcProjectId,
            parentField: field,
            parentFieldPath: [srcObjectId],
            maxDepth,
            currentDepth: 1
        };

        _.forEach(field.fields, (childField, childFieldName) => {
            res = iterateField({ field: childField, fieldPath: [srcObjectId, 'fields', childFieldName], fn, opts: childOpts });
            if (res === false) {
                return false;
            }
            return undefined;
        });

        return res;
    });
};
